import BasePlayer from './BasePlayer';
import { getNextPlayRequestId } from '../common/channelPlayerState';
import { getVideoObjectFitForElement } from '../common/channelVideoAspect';
import { markChannelStreamReady } from '../common/channelStreamUrl';

/**
 * Default/fallback player for unsupported devices or web browsers
 * Uses HTML5 video element for HTTP/HLS stream playback
 */
class DefaultPlayer extends BasePlayer {
  constructor() {
    super();
    this.videoElement = null;
    this._layoutSyncHandler = null;
    this._streamReadyHandler = null;
    this._currentPlayUrl = null;
  }

  supportsFullscreenToggle() {
    return true;
  }

  _getOrCreateVideoElement() {
    this.videoElement = document.getElementById('default-video-player');

    if (!this.videoElement) {
      console.warn('Default: video element not found in DOM');
      return null;
    }

    return this.videoElement;
  }

  _syncVideoLayout(video, isFullScreen) {
    if (!video) {
      return;
    }

    video.style.objectFit = getVideoObjectFitForElement(video);

    if (isFullScreen) {
      video.style.position = 'fixed';
      video.style.top = '0';
      video.style.left = '0';
      video.style.width = '100vw';
      video.style.height = '100vh';
      video.style.zIndex = '1';
      video.style.borderRadius = '0';
      return;
    }

    // Fill the preview panel (VideoPlayerArea). Re-applied after each play/zap
    // because stop() and native video sizing can clear inline dimensions.
    video.style.position = 'absolute';
    video.style.top = '0';
    video.style.left = '0';
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.zIndex = 'auto';
    video.style.borderRadius = '0';
  }

  _unbindLayoutSync(video) {
    if (!video) {
      return;
    }
    if (this._layoutSyncHandler) {
      video.removeEventListener('loadedmetadata', this._layoutSyncHandler);
      video.removeEventListener('canplay', this._layoutSyncHandler);
      video.removeEventListener('playing', this._layoutSyncHandler);
      this._layoutSyncHandler = null;
    }
    if (this._streamReadyHandler) {
      video.removeEventListener('playing', this._streamReadyHandler);
      video.removeEventListener('canplay', this._streamReadyHandler);
      this._streamReadyHandler = null;
    }
  }

  _bindLayoutSync(video, isFullScreen, myRequestId) {
    this._unbindLayoutSync(video);
    if (!video) {
      return;
    }

    var self = this;
    this._layoutSyncHandler = function () {
      if (myRequestId !== self.getPlayerState().playRequestId) {
        return;
      }
      self._syncVideoLayout(video, self.getPlayerState().displayFullScreen);
    };

    this._streamReadyHandler = function () {
      if (myRequestId !== self.getPlayerState().playRequestId) {
        return;
      }
      markChannelStreamReady(myRequestId);
    };

    video.addEventListener('loadedmetadata', this._layoutSyncHandler);
    video.addEventListener('canplay', this._layoutSyncHandler);
    video.addEventListener('playing', this._layoutSyncHandler);
    video.addEventListener('playing', this._streamReadyHandler);
    video.addEventListener('canplay', this._streamReadyHandler);
  }

  async play(url, isFullScreen = false) {
    console.log('Default: play() called with URL:', url, 'Fullscreen:', isFullScreen);

    const video = this._getOrCreateVideoElement();
    if (!video) {
      console.error('Default: Cannot play - video element not available');
      return;
    }

    if (this._currentPlayUrl !== url) {
      this._unbindLayoutSync(video);
    }
    this._currentPlayUrl = url;

    const myRequestId = getNextPlayRequestId();
    this.updatePlayerState({ url });

    try {
      video.pause();
      this._syncVideoLayout(video, isFullScreen);

      video.src = url;
      video.load();

      await this.delay(100);

      if (myRequestId !== this.getPlayerState().playRequestId) {
        console.warn('Default: Aborting playback - newer request in progress');
        return;
      }

      const playPromise = video.play();

      if (playPromise !== undefined) {
        await playPromise;
        console.log('Default: Playback started successfully');
      }

      if (myRequestId !== this.getPlayerState().playRequestId) {
        console.warn('Default: Aborting after play - newer request in progress');
        return;
      }

      if (video.readyState >= 3 && !video.paused) {
        markChannelStreamReady(myRequestId);
      }

      this._syncVideoLayout(video, isFullScreen);
      this._bindLayoutSync(video, isFullScreen, myRequestId);
    } catch (error) {
      console.error('Default: Error playing video:', error);
      throw error;
    }
  }

  async stop() {
    console.log('Default: stop() called');

    const video = this._getOrCreateVideoElement();
    if (!video) return;

    try {
      this._unbindLayoutSync(video);
      video.pause();
      video.currentTime = 0;
      video.src = '';
      video.load();

      this.videoElement = null;
      console.log('Default: Playback stopped');
    } catch (error) {
      console.error('Default: Error stopping video:', error);
    }
  }

  async setDisplayRect(coords) {
    console.log('Default: setDisplayRect() called with coords:', coords);

    const video = this._getOrCreateVideoElement();
    if (!video) return;

    const playerState = this.getPlayerState();
    if (playerState.displayFullScreen) {
      this._syncVideoLayout(video, true);
      return;
    }

    // Prefer filling the preview panel; viewport coords are for native avplay only.
    if (coords && coords.width > 0 && coords.height > 0) {
      const area =
        document.querySelector('[data-tizen-video-area]') || video.parentElement;
      if (area) {
        const areaRect = area.getBoundingClientRect();
        const x = (coords.x != null ? coords.x : coords.left) - areaRect.left;
        const y = (coords.y != null ? coords.y : coords.top) - areaRect.top;
        video.style.position = 'absolute';
        video.style.left = Math.round(x) + 'px';
        video.style.top = Math.round(y) + 'px';
        video.style.width = Math.round(coords.width) + 'px';
        video.style.height = Math.round(coords.height) + 'px';
        video.style.objectFit = getVideoObjectFitForElement(video);
        return;
      }
    }

    this._syncVideoLayout(video, false);
  }

  async toggleFullscreen() {
    console.log('Default: toggleFullscreen() called');

    const video = this._getOrCreateVideoElement();
    if (!video) return;

    const playerState = this.getPlayerState();
    const newFullScreenState = !playerState.displayFullScreen;
    this.updatePlayerState({ displayFullScreen: newFullScreenState });

    try {
      this._syncVideoLayout(video, newFullScreenState);
      const myRequestId = playerState.playRequestId;
      this._bindLayoutSync(video, newFullScreenState, myRequestId);
      if (newFullScreenState) {
        console.log('Default: Entered CSS fullscreen');
      } else {
        console.log('Default: Exited CSS fullscreen');
      }
    } catch (error) {
      console.error('Default: Error toggling fullscreen:', error);
    }
  }
}

export default DefaultPlayer;
